package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * Screenshot storage used and allowed, per project and per workspace.
 *
 * Bytes only. A count of captures grows with languages × slots × devices, which would be a charge per
 * target language in disguise (`PRICING.md`: no storage charge per word, target language or key).
 * Byte ceilings are an abuse guard for a small machine, not a tier. [captures] is informational.
 */
@Serializable
data class ScreenshotQuotaDto(
    val plan: Plan,
    val projectUsedBytes: Long,
    val projectLimitBytes: Long,
    val workspaceUsedBytes: Long,
    val workspaceLimitBytes: Long,
    val captures: Int,
)

object ScreenshotQuota {
    private const val MIB = 1024L * 1024L
    private const val GIB = 1024L * MIB

    /**
     * Per project. Free has one project, so its project and workspace ceilings are the same.
     * Sized so the maintainer's own apps (5 slots × 2 devices × 10 languages, previous kept) fit on
     * Studio. Open decision (ROADMAP-SCREENSHOTS §12, 2a): smaller while backups live on the NAS.
     */
    fun projectBytes(plan: Plan): Long = when (plan) {
        Plan.FREE -> 256 * MIB
        Plan.STUDIO -> 1 * GIB
        Plan.TEAM -> 2 * GIB
    }

    fun workspaceBytes(plan: Plan): Long = when (plan) {
        Plan.FREE -> 256 * MIB
        Plan.STUDIO -> 4 * GIB
        Plan.TEAM -> 16 * GIB
    }

    /** "1.9 GB", "256 MB": the units a refusal speaks in. */
    fun describe(bytes: Long): String = when {
        bytes >= GIB -> {
            val tenths = bytes * 10 / GIB
            if (tenths % 10 == 0L) "${tenths / 10} GB" else "${tenths / 10}.${tenths % 10} GB"
        }
        bytes >= MIB -> "${bytes / MIB} MB"
        else -> "${bytes / 1024} KB"
    }
}
